import { EMPTY_VALUE } from "staticConfiguration";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import PublishIcon from "@mui/icons-material/Publish";
import { Switch } from "@mui/material";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { ErrorComponent } from "core/components/ErrorComponent";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useLocale } from "core/hooks/useLocale";
import { usePageLogging } from "core/hooks/usePageLogging";
import { FavoriteButton } from "features/MyLinks/components";
import { useMarkNotificationReadByItemIdOnMount } from "features/Notifications/useMarkNotificationReadByItemIdOnMount";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import { CTAButton } from "../../components/CTAButton";
import { getImageSRC } from "../../utils";
import { ArticlePageLoadingSkeleton } from "../ArticlePageLoadingSkeleton";

export function SingleNewsPageView({ canEdit }: { canEdit: boolean }) {
  const {
    query: { id },
  } = useRouter();

  if (typeof id !== "string") {
    throw new Error(
      "This component must be used on a route with a dynamic [id] segment"
    );
  }
  const locale = useLocale();
  useMarkNotificationReadByItemIdOnMount(id);
  usePageLogging({
    itemType: "News",
    itemId: String(id),
    componentName: "SingleNewsPage",
  });
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:SingleNewsPage",
  });
  const [editMode, setEditMode] = useQueryState(
    "edit",
    parseAsBoolean.withDefault(false)
  );
  const { data, isError, isLoading } = useCentrixApi({
    path: `/api/news-events/articles-v2/News/public/{id}`,
    parameters: {
      query: { Locale: locale ?? "" },
      path: { id: id as string },
    },
  });

  const formatDateWithTimeZone = useFormatDate({
    timeZoneName: "short",
    hour: "2-digit",
    minute: "2-digit",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  if (isError) {
    return <ErrorComponent apiError={isError} />;
  }
  if (isLoading || !data) {
    return <ArticlePageLoadingSkeleton />;
  }

  const {
    publishedTime,
    banner,
    articleCategory,
    articleType,
    content,
    title,
    tags = [],
    buttonText,
    buttonUrl,
    isImportant,
  } = data;

  const blurDataUrl = banner?.blurDataUrl ?? "";
  const altText = banner?.altText ?? "";
  const blobName = banner?.blobName ?? "";
  const description = banner?.description ?? "";
  const height = banner?.height ?? 0;
  const width = banner?.width ?? 0;

  const imageSRC = getImageSRC(blobName);
  const formattedPublish = t("publishedTime", {
    date: publishedTime ? formatDateWithTimeZone(publishedTime) : EMPTY_VALUE,
  });
  return (
    <PageContainer maxWidth="md">
      <PageHeader
        TopSlot={
          <Breadcrumbs separator=">">
            <CustomLink href="/news">{t("newsLink")}</CustomLink>
            {articleCategory ? (
              <CustomLink href={`/news?categories=${articleCategory.id}`}>
                {articleCategory.title}
              </CustomLink>
            ) : (
              <CustomLink href="/news?categories=uncategorized">
                {t("uncategorized")}
              </CustomLink>
            )}
            <span />
          </Breadcrumbs>
        }
        TitleSlot={
          <FavoriteButton
            itemType="News"
            itemId={String(id)}
            title={title ?? ""}
          />
        }
        RightSlot={
          canEdit && (
            <Stack alignItems="end">
              <FormControlLabel
                control={<Switch />}
                checked={editMode}
                onChange={(_, checked) => setEditMode(checked)}
                labelPlacement="start"
                label={t("editToggle")}
              />
            </Stack>
          )
        }
        title={title ?? ""}
        titleStackProps={{ mb: 1 }}
        subTitle={
          <Box
            mt={1}
            ml={1}
            display="grid"
            gridTemplateColumns="auto 1fr"
            columnGap={1}
            rowGap={0.5}
            alignItems="center"
            color="text.secondary"
          >
            <PublishIcon fontSize="small" color="inherit" />
            <Typography variant="body2">{formattedPublish}</Typography>
          </Box>
        }
      />
      <Box position="relative">
        <Image
          src={imageSRC}
          alt={altText}
          blurDataURL={blurDataUrl}
          placeholder="blur"
          layout="responsive"
          height={height}
          width={width}
          objectFit="contain"
        />
        {isImportant && (
          <PriorityHighRoundedIcon
            sx={{
              zIndex: 1,
              position: "absolute",
              top: ({ spacing }) => spacing(1),
              left: ({ spacing }) => spacing(1),
              color: "red",
            }}
          />
        )}
        {articleType && (
          <CustomLink href={`/news?types=${articleType.id}`}>
            <Chip
              clickable
              color="primary"
              label={articleType.title}
              sx={{
                zIndex: 1,
                position: "absolute",
                top: ({ spacing }) => spacing(2),
                right: ({ spacing }) => spacing(2),
              }}
            />
          </CustomLink>
        )}
      </Box>
      <Typography
        sx={{ mt: 1, ml: 1 }}
        variant="caption"
        color="text.secondary"
      >
        {description}
      </Typography>
      <Divider sx={{ my: 4 }} />
      {/** biome-ignore lint/security/noDangerouslySetInnerHtml: this content is generated by a trusted source */}
      <Box dangerouslySetInnerHTML={{ __html: content ?? "" }} />
      {buttonText && buttonUrl && (
        <Stack my={4} direction="row" justifyContent="center">
          <CTAButton href={buttonUrl} text={buttonText} />
        </Stack>
      )}
      <Divider sx={{ my: 4 }} />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-evenly"
        alignItems="flex-start"
      >
        {Array.isArray(tags) && tags.length > 0 && (
          <Stack spacing={1} justifyContent="center">
            <Typography variant="body1" textAlign="center">
              {t("tags")}
            </Typography>
            <Stack spacing={1} justifyContent="center" direction="row">
              {tags.map((tag) => (
                <CustomLink key={tag} href={`/news?tags=${tag}`}>
                  #{tag}
                </CustomLink>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
}
